/**
 * components/platform/zoneSelect.tsx
 *
 * SKPLATFORM-UICC-006 C-2 / DB-006 S1 — choosing an organisation's residency
 * zone at provisioning.
 *
 * Chosen ONCE, at creation, and never changeable afterwards. The interaction
 * has to carry that weight without being melodramatic about it.
 *
 * ── THE THREE RULES ───────────────────────────────────────────────────────
 *
 * 1. A SELECT, not free text. The vocabulary is closed and a typo in an
 *    immutable jurisdictional field is not a recoverable mistake.
 *
 * 2. NO DEFAULT PRE-SELECTED. A defaulted immutable field is how the wrong
 *    value ships — the form does not submit until a zone is chosen. (Note the
 *    contrast with the database, which DOES default to `in`: that default
 *    exists for the backfill of pre-existing orgs, not for new ones.)
 *
 * 3. THE CONSEQUENCE IS ALWAYS VISIBLE, never a tooltip. This is the one field
 *    on the form whose consequence a reader must not have to hover to
 *    discover.
 *
 * Zones with no identity deployment yet are shown and SELECTABLE — provisioning
 * ahead of deployment is legitimate — but marked, because the reader should
 * still know.
 *
 * There is deliberately no "change zone" affordance anywhere in the product;
 * see residency.tsx for why an absent control beats a disabled one.
 */

import React from 'react';
import { View, Text, Pressable } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { FS, FW, R } from '../../utils/platformTokens';
import type { ResidencyZone } from '../../schema/platform/types';

/**
 * The closed vocabulary, as a fallback for when the register read fails.
 *
 * Kept in step with `identity.residency_zone`, which is the authority — this
 * exists so a provisioning engineer is not blocked by a reference-data read,
 * not as a second source of truth.
 */
export const FALLBACK_ZONES: ResidencyZone[] = [
  { code: 'ae', display_name: 'United Arab Emirates', processing_region: 'me-central-1', governing_regime: 'PDPL', passport_issuer: 'passport.ae.softknack.com', is_deployed: true },
  { code: 'in', display_name: 'India', processing_region: 'asia-south1', governing_regime: 'DPDP 2023', passport_issuer: 'passport.in.softknack.com', is_deployed: true },
  { code: 'gb', display_name: 'United Kingdom', processing_region: null, governing_regime: 'UK GDPR', passport_issuer: null, is_deployed: false },
  { code: 'au', display_name: 'Australia', processing_region: 'australia-southeast1', governing_regime: 'Privacy Act 1988', passport_issuer: null, is_deployed: false },
];

export const ZoneSelect: React.FC<{
  zones?: ResidencyZone[];
  /** `null` means NOTHING is selected. Not a sentinel for "default". */
  value: string | null;
  onChange: (code: string) => void;
}> = ({ zones = FALLBACK_ZONES, value, onChange }) => {
  const { theme } = useTheme();
  const c = theme.colors;

  return (
    <View>
      <Text style={{ fontSize: FS.sm, color: c.ink2, fontWeight: FW.medium, marginBottom: 8 }}>
        Data residency zone
      </Text>

      <View style={{ gap: 6 }}>
        {zones.map((z) => {
          const selected = value === z.code;
          return (
            <Pressable
              key={z.code}
              onPress={() => onChange(z.code)}
              accessibilityRole="radio"
              accessibilityState={{ selected }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 10,
                paddingVertical: 10,
                paddingHorizontal: 12,
                borderRadius: R.r2,
                borderWidth: 1,
                borderColor: selected ? c.accent : c.line,
                backgroundColor: selected ? c.accentSoft : c.card,
              }}
            >
              <View
                style={{
                  width: 14, height: 14, borderRadius: 7, borderWidth: 1,
                  borderColor: selected ? c.accent : c.lineStrong,
                  backgroundColor: selected ? c.accent : 'transparent',
                }}
              />
              {/* Code in mono, name in sans — the code is the value a reader
                  might compare against a token claim or a log line. */}
              <Text style={{ fontSize: FS.sm, color: c.ink, fontFamily: theme.font.mono, width: 34 }}>
                {z.code}
              </Text>
              <Text style={{ fontSize: FS.sm, color: c.ink, flex: 1 }}>{z.display_name}</Text>
              {!z.is_deployed && (
                /* Selectable, but the reader should know. */
                <Text style={{ fontSize: FS.xs, color: c.ink4 }}>no identity deployment yet</Text>
              )}
            </Pressable>
          );
        })}
      </View>

      {/* Always visible. Never a tooltip. */}
      <Text style={{ fontSize: FS.xs, color: c.ink3, marginTop: 10, lineHeight: 18 }}>
        Set once. Changing an organisation&apos;s zone later means relocating every customer
        identity record across a jurisdictional boundary — that is a migration, not a setting.
      </Text>
    </View>
  );
};

/**
 * The confirmation step.
 *
 * This is the ONLY field on the provisioning form that gets one, and that
 * singularity is what makes it register — a form where everything is confirmed
 * is a form where nothing is read.
 *
 * The button NAMES THE ZONE. A button reading only "Confirm" can be clicked
 * without reading; a button reading "Create in ae" cannot.
 */
export const ZoneConfirmBody: React.FC<{ zone: ResidencyZone }> = ({ zone }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View>
      <Text style={{ fontSize: FS.sm, color: c.ink2, lineHeight: 20 }}>
        You are creating this organisation in zone{' '}
        <Text style={{ fontFamily: theme.font.mono, color: c.ink, fontWeight: FW.medium }}>
          {zone.code}
        </Text>
        {'  ·  '}
        {zone.display_name}.
      </Text>
      <Text style={{ fontSize: FS.sm, color: c.ink2, lineHeight: 20, marginTop: 10 }}>
        Customer identity for this organisation will be processed in that zone and cannot be moved
        later.
      </Text>
    </View>
  );
};
